/**
 * Client-side search, filtering, sorting and pagination for the Users table.
 * In memory rather than SQL because `Account.users` has no server-side
 * filtering/pagination, user lists are small, and the full list is already loaded.
 *
 * Moving it server-side is more than "paginate the users query": it needs a NEW
 * paginated field (~20 callers rely on `Account.users`), per-team counts read from
 * `Team.members(...).totalCount`, and the paginated `totalCount` for "All users".
 */
import { SelectOption } from '@payaca/ambrosia';
import { SortDirection } from '@payaca/types/listViewTypes';
import { useEffect, useMemo, useReducer } from 'react';

import { MyAccountUsersQuery } from '@/gql/graphql';

export const USERS_PAGE_SIZE = 25;

export type AccountUser =
  MyAccountUsersQuery['me']['user']['account']['users'][number];

export type UsersSortableField = 'fullName' | 'email' | 'systemRole' | 'status';

export type TeamListItem = { id: string; name: string; count: number };

export type TeamOption = { id: string; name: string };

const compareBy = (
  a: AccountUser,
  b: AccountUser,
  field: UsersSortableField
) => {
  const aValue = (a[field] ?? '').toLowerCase();
  const bValue = (b[field] ?? '').toLowerCase();
  return aValue.localeCompare(bValue);
};

/** Unique, sorted `{ label, value }` options from a list of string values. */
const toSelectOptions = (values: string[]): SelectOption<string>[] =>
  [...new Set(values)].sort().map((value) => ({ label: value, value }));

type FiltersState = {
  searchTerm: string;
  appliedSearchTerm: string;
  statusFilter: string[];
  roleFilter: string[];
  selectedTeamId: string | null;
  sortBy?: UsersSortableField;
  sortDirection: SortDirection;
  pageNumber: number;
};

type FiltersAction =
  | { type: 'setSearchTerm'; value: string }
  | { type: 'applySearch'; value: string }
  | { type: 'setStatusFilter'; value: string[] }
  | { type: 'setRoleFilter'; value: string[] }
  | { type: 'selectTeam'; teamId: string | null }
  | { type: 'setSort'; field: UsersSortableField; direction: SortDirection }
  | { type: 'setPage'; page: number };

const INITIAL_STATE: FiltersState = {
  searchTerm: '',
  appliedSearchTerm: '',
  statusFilter: [],
  roleFilter: [],
  selectedTeamId: null,
  sortDirection: SortDirection.ASCENDING,
  pageNumber: 1,
};

// Any change that narrows the result set resets to page 1, so a future filter
// can't forget to. Only the live search term and explicit page changes don't.
const filtersReducer = (
  state: FiltersState,
  action: FiltersAction
): FiltersState => {
  switch (action.type) {
    case 'setSearchTerm':
      return { ...state, searchTerm: action.value };
    case 'applySearch':
      return { ...state, appliedSearchTerm: action.value, pageNumber: 1 };
    case 'setStatusFilter':
      return { ...state, statusFilter: action.value, pageNumber: 1 };
    case 'setRoleFilter':
      return { ...state, roleFilter: action.value, pageNumber: 1 };
    case 'selectTeam':
      return { ...state, selectedTeamId: action.teamId, pageNumber: 1 };
    case 'setSort':
      return {
        ...state,
        sortBy: action.field,
        sortDirection: action.direction,
        pageNumber: 1,
      };
    case 'setPage':
      return { ...state, pageNumber: action.page };
  }
};

export const useUsersTableFilters = (
  users: AccountUser[],
  teams: TeamOption[]
) => {
  const [state, dispatch] = useReducer(filtersReducer, INITIAL_STATE);
  const {
    searchTerm,
    appliedSearchTerm,
    statusFilter,
    roleFilter,
    selectedTeamId,
    sortBy,
    sortDirection,
    pageNumber,
  } = state;

  // From the server, so empty teams still appear. Counts are active members,
  // computed from the loaded users so they match the table.
  const teamListItems = useMemo<TeamListItem[]>(() => {
    const activeCountByTeamId = new Map<string, number>();
    users.forEach((user) =>
      user.teams.items.forEach((team) => {
        activeCountByTeamId.set(
          team.id,
          (activeCountByTeamId.get(team.id) ?? 0) + 1
        );
      })
    );
    return teams
      .map((team) => ({
        id: team.id,
        name: team.name,
        count: activeCountByTeamId.get(team.id) ?? 0,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [users, teams]);

  const anyUserHasTeam = users.some((user) => user.teams.items.length > 0);

  const allUsersCount = users.length;

  // So the pane heading and the filtered table can't disagree.
  useEffect(() => {
    if (selectedTeamId && !teams.some((team) => team.id === selectedTeamId)) {
      dispatch({ type: 'selectTeam', teamId: null });
    }
  }, [selectedTeamId, teams]);

  const statusOptions = useMemo(
    () => toSelectOptions(users.map((user) => user.status)),
    [users]
  );

  const roleOptions = useMemo(
    () => toSelectOptions(users.map((user) => user.systemRole)),
    [users]
  );

  const filteredUsers = useMemo(() => {
    const term = appliedSearchTerm.trim().toLowerCase();

    const result = users.filter((user) => {
      if (
        term &&
        !(user.fullName ?? '').toLowerCase().includes(term) &&
        !(user.email ?? '').toLowerCase().includes(term)
      ) {
        return false;
      }
      if (statusFilter.length && !statusFilter.includes(user.status)) {
        return false;
      }
      if (roleFilter.length && !roleFilter.includes(user.systemRole)) {
        return false;
      }
      if (
        selectedTeamId &&
        !user.teams.items.some((team) => team.id === selectedTeamId)
      ) {
        return false;
      }
      return true;
    });

    if (sortBy) {
      result.sort((a, b) => {
        const comparison = compareBy(a, b, sortBy);
        return sortDirection === SortDirection.ASCENDING
          ? comparison
          : -comparison;
      });
    }

    return result;
  }, [
    users,
    appliedSearchTerm,
    statusFilter,
    roleFilter,
    selectedTeamId,
    sortBy,
    sortDirection,
  ]);

  const pageCount = Math.max(
    1,
    Math.ceil(filteredUsers.length / USERS_PAGE_SIZE)
  );

  // So a shrinking result set never strands the user on an empty page.
  useEffect(() => {
    if (pageNumber > pageCount) {
      dispatch({ type: 'setPage', page: pageCount });
    }
  }, [pageNumber, pageCount]);

  const currentPage = Math.min(pageNumber, pageCount);

  const pagedUsers = useMemo(() => {
    const start = (currentPage - 1) * USERS_PAGE_SIZE;
    return filteredUsers.slice(start, start + USERS_PAGE_SIZE);
  }, [filteredUsers, currentPage]);

  // All active users, not the filtered set, so the columns stay stable.
  const anyActiveUserHasCostPerHour = users.some((u) => u.costPerHour !== null);
  const anyActiveUserHasSortOrder = users.some((u) => u.sortOrder !== null);

  return {
    // search
    searchTerm,
    onSearchTermChange: (value: string) =>
      dispatch({ type: 'setSearchTerm', value }),
    applySearch: (value: string) => dispatch({ type: 'applySearch', value }),
    // team list
    selectedTeamId,
    onSelectTeam: (teamId: string | null) =>
      dispatch({ type: 'selectTeam', teamId }),
    teamListItems,
    anyUserHasTeam,
    allUsersCount,
    // filters
    roleFilter,
    onRoleFilterChange: (value: string[]) =>
      dispatch({ type: 'setRoleFilter', value }),
    roleOptions,
    statusFilter,
    onStatusFilterChange: (value: string[]) =>
      dispatch({ type: 'setStatusFilter', value }),
    statusOptions,
    // sorting
    sortBy,
    sortDirection,
    onSort: (field: UsersSortableField, direction: SortDirection) =>
      dispatch({ type: 'setSort', field, direction }),
    // pagination
    pageNumber: currentPage,
    onPageChange: (page: number) => dispatch({ type: 'setPage', page }),
    pageSize: USERS_PAGE_SIZE,
    // results
    filteredUsers,
    pagedUsers,
    anyActiveUserHasCostPerHour,
    anyActiveUserHasSortOrder,
  };
};
